/**
 * ClientLocationDetailPage — full profile for a single client
 * location, including its linked Google Business Profile info,
 * live "view on Google" link, review count/rating, and the flat
 * feed of reviews for that specific branch.
 *
 * Powered by the existing `/api/clients/{cid}/locations/{lid}/gmb/reviews`
 * endpoint which already returns both the `branch` document and its
 * reviews together.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Star, ArrowLeft, MapPin, Phone, Mail, Globe2, Loader2,
  ExternalLink, RefreshCw, MessageSquare, Building2, Map,
} from "lucide-react";

const errMsg = (e, fb) => e?.response?.data?.detail || fb;
const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—"
    : d.toLocaleDateString("en-AU",
      { day: "2-digit", month: "short", year: "numeric" });
};
const fmtDateTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—"
    : d.toLocaleString("en-AU",
      { day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit" });
};

const STAR_TO_INT = { STAR_RATING_UNSPECIFIED: 0, ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };
const starInt = (r) => typeof r?.star_rating === "number"
  ? r.star_rating
  : STAR_TO_INT[r?.star_rating] ?? 0;

function Stars({ n }) {
  const filled = Math.max(0, Math.min(5, Number(n) || 0));
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i}
          className={`h-4 w-4 ${i <= filled
            ? "fill-amber-500 text-amber-500"
            : "text-zinc-300"}`} />
      ))}
    </span>
  );
}

function Field({ label, value, mono, testid }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-start gap-2 text-sm py-1"
      data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-zinc-500 w-32 shrink-0 pt-0.5">{label}</div>
      <div className={`text-zinc-800 break-all ${mono ? "font-mono text-xs" : ""}`}>
        {value}
      </div>
    </div>
  );
}

export default function ClientLocationDetailPage() {
  const { clientId, locationId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(
        `/clients/${clientId}/locations/${locationId}/gmb/reviews?limit=200`);
      setData(d);
    } catch (e) {
      toast.error(errMsg(e, "Failed to load location"));
    } finally { setLoading(false); }
  }, [clientId, locationId]);

  useEffect(() => { load(); }, [load]);

  const syncReviews = async () => {
    setSyncing(true);
    try {
      const { data: r } = await api.post(
        `/clients/${clientId}/locations/${locationId}/gmb/sync-reviews`);
      toast.success(`Synced ${r.review_count || 0} review${r.review_count === 1 ? "" : "s"}`);
      await load();
    } catch (e) { toast.error(errMsg(e, "Sync failed")); }
    finally { setSyncing(false); }
  };

  if (loading && !data) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-zinc-500"
        data-testid="location-detail-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading location…
      </div>
    );
  }

  const branch = data?.branch || {};
  const reviews = data?.reviews || [];
  const linked = !!(branch.gmb_account_id && branch.gmb_location_id);
  const addr = [branch.address_line1, branch.address_line2, branch.city,
                branch.state, branch.postcode, branch.country]
    .filter(Boolean).join(", ");

  return (
    <div className="p-6 space-y-5 max-w-[1200px]" data-testid="location-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm"
          onClick={() => navigate(`/clients/${clientId}/locations`)}
          data-testid="location-detail-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Locations
        </Button>
        <div className="flex-1">
          <div className="label-overline">Location</div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display font-extrabold text-2xl">
              {branch.name || "—"}
            </h1>
            {linked ? (
              <Badge variant="outline"
                className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200">
                <Star className="h-3 w-3 mr-1 fill-emerald-700" /> GBP linked
              </Badge>
            ) : (
              <Badge variant="outline"
                className="rounded-sm bg-amber-50 text-amber-800 border-amber-200">
                Not GBP linked
              </Badge>
            )}
          </div>
        </div>
        {linked && (
          <Button variant="outline" size="sm" onClick={syncReviews}
            disabled={syncing} data-testid="location-detail-sync">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${syncing ? "animate-spin" : ""}`} />
            Sync from Google
          </Button>
        )}
      </div>

      {/* Headline metrics for GBP-linked locations */}
      {linked && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Card className="rounded-sm">
            <CardContent className="p-4">
              <div className="label-overline">Google rating</div>
              <div className="flex items-center gap-2 mt-1">
                <div className="font-display font-extrabold text-3xl"
                  data-testid="location-avg-rating">
                  {branch.gmb_average_rating
                    ? Number(branch.gmb_average_rating).toFixed(1)
                    : "—"}
                </div>
                <Stars n={branch.gmb_average_rating || 0} />
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-sm">
            <CardContent className="p-4">
              <div className="label-overline">Review count</div>
              <div className="font-display font-extrabold text-3xl mt-1"
                data-testid="location-review-count">
                {Number(branch.gmb_review_count || 0).toLocaleString()}
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-sm">
            <CardContent className="p-4">
              <div className="label-overline">Last synced</div>
              <div className="font-mono text-sm mt-1"
                data-testid="location-last-sync">
                {fmtDateTime(branch.gmb_last_synced_at)}
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-sm">
            <CardContent className="p-4">
              <div className="label-overline">Live on Google</div>
              {branch.gmb_review_url ? (
                <a href={branch.gmb_review_url} target="_blank" rel="noreferrer"
                  className="text-sm text-[#002FA7] hover:underline inline-flex items-center gap-1 mt-2"
                  data-testid="location-live-link">
                  Open reviews page <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                <div className="text-[11px] text-zinc-400 mt-2">No public URL</div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Location profile */}
      <Card className="rounded-sm">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="h-4 w-4 text-zinc-700" />
            <h3 className="font-display font-bold text-base">Location profile</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <div>
              <Field label="Type" value={branch.location_type} testid="loc-field-type" />
              <Field label="Phone" value={branch.phone} testid="loc-field-phone" />
              <Field label="Email" value={branch.email} testid="loc-field-email" />
              <Field label="Address" value={addr || "—"} testid="loc-field-address" />
              <Field label="Website" value={branch.website && (
                <a href={branch.website} target="_blank" rel="noreferrer"
                  className="text-[#002FA7] hover:underline inline-flex items-center gap-1">
                  {branch.website} <ExternalLink className="h-3 w-3" />
                </a>
              )} testid="loc-field-website" />
            </div>
            <div>
              <Field label="GMB Account ID" value={branch.gmb_account_id} mono
                testid="loc-field-gmb-account" />
              <Field label="GMB Location ID" value={branch.gmb_location_id} mono
                testid="loc-field-gmb-location" />
              <Field label="Google Place ID" value={branch.gmb_place_id} mono
                testid="loc-field-place" />
              <Field label="Latitude" value={branch.latitude} mono />
              <Field label="Longitude" value={branch.longitude} mono />
            </div>
          </div>
          {branch.latitude && branch.longitude && (
            <div className="mt-3 pt-3 border-t border-zinc-100">
              <a href={`https://www.google.com/maps/search/?api=1&query=${branch.latitude},${branch.longitude}`}
                target="_blank" rel="noreferrer"
                className="text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1"
                data-testid="loc-map-link">
                <Map className="h-3 w-3" /> Open in Google Maps
              </a>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reviews */}
      {linked && (
        <Card className="rounded-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="h-4 w-4 text-zinc-700" />
              <h3 className="font-display font-bold text-base">
                {reviews.length} synced review{reviews.length === 1 ? "" : "s"}
              </h3>
              <Link to={`/clients/${clientId}/google-reviews`}
                className="text-xs text-[#002FA7] hover:underline ml-auto"
                data-testid="loc-view-all-reviews">
                View all client reviews →
              </Link>
            </div>
            {reviews.length === 0 ? (
              <div className="text-sm text-zinc-400 italic py-3 text-center">
                No reviews synced yet. Click <b>Sync from Google</b> above.
              </div>
            ) : (
              <div className="space-y-3">
                {reviews.map((r, i) => (
                  <div key={r.review_id || `r-${i}`}
                    className="border border-zinc-100 rounded-sm p-3"
                    data-testid={`loc-review-${r.review_id || i}`}>
                    <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">
                          {r.reviewer_display_name || "Anonymous"}
                        </span>
                        <Stars n={starInt(r)} />
                      </div>
                      <span className="text-[10px] text-zinc-400 font-mono">
                        {fmtDate(r.create_time)}
                      </span>
                    </div>
                    {r.comment && (
                      <div className="text-[13px] text-zinc-700 whitespace-pre-wrap mt-1">
                        {r.comment}
                      </div>
                    )}
                    {r.reply_comment && (
                      <div className="mt-2 pl-3 border-l-2 border-emerald-200 bg-emerald-50/40 rounded-r-sm p-2">
                        <div className="text-[10px] uppercase tracking-wide text-emerald-700 mb-0.5">
                          Reply · {fmtDate(r.reply_update_time)}
                        </div>
                        <div className="text-[12px] text-zinc-700 whitespace-pre-wrap">
                          {r.reply_comment}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
